// EPUB 렌더러 — epub.js 래퍼 (기획서 8.1)
// MVP-1: 파일 추가/읽기 + CFI 위치 저장. iframe 브릿지(테마 주입·키보드 전달)는 MVP-2(8.5).
import ePub from 'epubjs';

export class EpubReader {
  constructor(container) {
    this.container = container;
    this.book = null;
    this.rendition = null;
    this._onPositionChange = null;
    this._onKey = null;
  }

  onPositionChange(fn) {
    this._onPositionChange = fn;
  }

  /**
   * @param {Blob} blob   .epub 파일
   * @param {string|null} cfi  복원할 CFI (없으면 처음부터)
   */
  async open(blob, cfi) {
    const buf = await blob.arrayBuffer();
    this.book = ePub(buf);
    this.rendition = this.book.renderTo(this.container, {
      width: '100%',
      height: '100%',
      flow: 'paginated', // 좌우 페이지 넘김 (기획서 8.1)
      spread: 'none', // MVP-1 단페이지 강제
      allowScriptedContent: false,
    });

    await this.book.ready;

    // 위치 복원: CFI 우선, 실패 시 처음부터 (percent fallback 은 호출부에서 보정)
    try {
      await this.rendition.display(cfi || undefined);
    } catch {
      await this.rendition.display();
    }

    // 위치 변경 → CFI + percent 통지 (기획서 9.3)
    this.rendition.on('relocated', (location) => {
      if (!this._onPositionChange) return;
      const cur = location && location.start ? location.start.cfi : null;
      let percent = 0;
      try {
        if (this.book.locations && this.book.locations.length()) {
          percent = this.book.locations.percentageFromCfi(cur);
        } else if (location && location.start) {
          percent = location.start.percentage || 0;
        }
      } catch {
        percent = (location && location.start && location.start.percentage) || 0;
      }
      this._onPositionChange(cur, percent);
    });

    // iframe 키 이벤트를 메인으로 위임 (기획서 8.5.1 최소 구현)
    this.rendition.on('keyup', (e) => {
      if (this._onKey) this._onKey(e);
    });

    // 백그라운드에서 percent 계산용 locations 생성 (대략 1024자 단위)
    this.book.locations.generate(1024).catch(() => {});
  }

  async nextPage() {
    if (this.rendition) await this.rendition.next();
  }
  async prevPage() {
    if (this.rendition) await this.rendition.prev();
  }

  async goToCfi(cfi) {
    if (this.rendition && cfi) {
      try {
        await this.rendition.display(cfi);
      } catch {
        /* 실패 시 호출부가 percent fallback 처리 */
      }
    }
  }

  async goToPercent(percent) {
    if (!this.rendition || !this.book) return;
    try {
      const cfi = this.book.locations.cfiFromPercentage(percent);
      if (cfi) await this.rendition.display(cfi);
    } catch {
      /* noop */
    }
  }

  onKey(fn) {
    this._onKey = fn;
  }

  // 6.1 테마 주입 (기획서 8.5.2 최소 구현 — MVP-2 에서 글꼴/줄간격까지 확장)
  applyTheme({ bg, fg }) {
    if (!this.rendition) return;
    this.rendition.themes.override('color', fg);
    this.rendition.themes.override('background', bg);
  }

  resize() {
    if (this.rendition) this.rendition.resize();
  }

  destroy() {
    try {
      if (this.rendition) this.rendition.destroy();
      if (this.book) this.book.destroy();
    } catch {
      /* noop */
    }
    this.container.innerHTML = '';
    this.book = null;
    this.rendition = null;
    this._onPositionChange = null;
  }
}
